/**
 * CYP ADHD Diagnostic Decision routing.
 *
 * The CYP DD pages are literal copies of the adult flat-DD pages (Eve/Sai,
 * 28 Aug: match the adult v2 workflow, do not invent new interfaces). All of
 * the eligibility/completeness logic is shared verbatim from
 * utils/diagnosticDecisionRouting — only the route builders differ, because
 * the CYP module lives under /proforma/cyp-adhd/diagnostic-decision/*.
 */

export {
  ADHD_CONDITION_LABEL,
  TOC_DIFFERENTIALS_STEP_TITLE,
  FLAT_OTHER_CONDITIONS_STEP_TITLE,
  getFlatOtherConditionsStepTitle,
  FINAL_FORMULATION_STEP_TITLE,
  SLAM_FINAL_FORMULATION_STEP_TITLE,
  getFinalFormulationStepTitle,
  getSecondaryComorbidDiagnoses,
  getDifferentialDiagnoses,
  getActionableOtherConditions,
  hasActionableOtherConditions,
  isAdhdPositiveDecision,
  isAdhdDecisionValid,
  isOtherConditionsComplete,
  computeDDModuleProgress,
  buildDDNavSections,
} from '../../../utils/diagnosticDecisionRouting';

/** Absolute routes for each CYP ADHD DD step. */
export const getDDStepRoutes = (patientId) => ({
  mdtReview: `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/mdt-review`,
  adhd: `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/adhd`,
  otherConditions: `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/other-conditions`,
  finalFormulation: `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/final-formulation`,
  // MDT Plan is descoped for CYP (22 Aug); route kept for shape parity so the
  // copied pages' dead branches resolve to something CYP-scoped, not adult.
  mdtPlan: `/patient/${patientId}/proforma/cyp-adhd/diagnostic-decision/final-formulation`,
});

/**
 * Maps a DDSideNavbar section index to its CYP route. Same index contract as
 * the adult getDDRouteForSectionIndex (see that function's bug history).
 */
export const getDDRouteForSectionIndex = (patientId, index, hasOther, hasMdtPlan = false) => {
  const routes = getDDStepRoutes(patientId);
  const ordered = [routes.mdtReview, routes.adhd];
  if (hasOther) ordered.push(routes.otherConditions);
  ordered.push(routes.finalFormulation);
  if (hasMdtPlan) ordered.push(routes.mdtPlan);
  return ordered[index] || routes.mdtReview;
};

/**
 * Where the CYP DD module starts from Patient Overview. Mirrors the adult
 * getDiagnosticDecisionStartRoute for the multi-step flat model (the only
 * shape CYP uses: hasMultiStepDD, flat differentials, no MDT Plan).
 */
export const getCypDiagnosticDecisionStartRoute = ({ patientId, patientDataModel, isFlatDifferentials = true }) => {
  const routes = getDDStepRoutes(patientId);
  const ddStage = patientDataModel?.dataModel?.['4_diagnosticDecision'];
  const primarySectionDone = ddStage?.sections?.['1']?.status === 'completed';
  const stageComplete = ddStage?.status === 'completed';

  if (!stageComplete) {
    const ddEntered = ddStage?.status === 'inProgress' || primarySectionDone;
    if (!ddEntered) return routes.mdtReview;
    return routes.adhd;
  }

  return routes.adhd;
};
